/**
 * @file 学んだ日の暦に使う記録をまとめて読む hook。
 *
 * @what: 直近 STUDY_DAYS_WEEKS 週ぶんの読んだ時間、家族の復習カード (まなぶ人で絞る)、まなぶ人の AI に
 *   聞いた記録を読み、呼び手が渡す取り組みの記録と学びメモと合わせて日ごとの活動 (model/studyDays.ts)
 *   にする。取り組みの記録とメモを呼び手から受けるのは、本だなが同じものを別の用途で読んでいるためである。
 * @why: 学んだ日は5つの記録から決めるので、読み取りを1か所にまとめ、暦の部品は純粋な描画にする。
 * @failure: 読んだ時間の取得に失敗したら `error` (暦は出さない)。ほかの記録の取得失敗は、その記録が
 *   無いものとして暦を出す (詳細は console)。※なぜなら読んだ時間が本体で、ほかは補助の手がかりのためである。
 */

import { useEffect, useMemo, useState } from "react";
import { fetchAskRecords, type StudyAskRecordSummary } from "../api/askRecords";
import { addDaysLocal, todayLocal } from "../api/dates";
import type { StudyNote } from "../api/notes";
import type { StudyProgressLog } from "../api/plans";
import { fetchReviewCards, type StudyReviewCard } from "../api/reviewCards";
import { STUDY_DAYS_WEEKS, buildStudyDays, weekStartOf, type StudyDay } from "../model/studyDays";
import { notesForLearner } from "../model/studyNoteContext";
import { useReadingSessions } from "./useReadingSessions";

export type StudyDaysState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "ready"; days: ReadonlyMap<string, StudyDay>; today: string; from: string }
  | { status: "error" };

export function useStudyDays(
  familyId: string,
  personId: string | null,
  /** まなぶ人の取り組みの記録 (直近の分。期間の外は中で落とす)。 */
  logs: readonly StudyProgressLog[],
  /** 家族の学びメモ (まなぶ人での絞り込みは中で行う)。 */
  notes: readonly StudyNote[],
  enabled = true,
): StudyDaysState {
  // 期間は今日を含む週の月曜から STUDY_DAYS_WEEKS 週さかのぼる。日付は描画のたびに変えない (取得の再実行を避ける)
  const [today] = useState(todayLocal);
  const from = useMemo(() => addDaysLocal(weekStartOf(today), -7 * (STUDY_DAYS_WEEKS - 1)), [today]);
  const sessions = useReadingSessions(familyId, personId, from, today, enabled);
  const [cards, setCards] = useState<readonly StudyReviewCard[]>([]);
  const [asks, setAsks] = useState<readonly StudyAskRecordSummary[]>([]);

  useEffect(() => {
    setCards([]);
    setAsks([]);
    if (!enabled || !personId) return;
    const controller = new AbortController();
    fetchReviewCards(familyId, controller.signal)
      .then((list) => {
        if (!controller.signal.aborted)
          setCards(list.filter((c) => c.learner_person_id === personId));
      })
      .catch((err: unknown) => {
        if (!controller.signal.aborted)
          console.error("[study] failed to load review cards for study days", err);
      });
    fetchAskRecords(familyId, personId, controller.signal)
      .then((list) => {
        if (!controller.signal.aborted) setAsks(list);
      })
      .catch((err: unknown) => {
        if (!controller.signal.aborted)
          console.error("[study] failed to load ask records for study days", err);
      });
    return () => controller.abort();
  }, [familyId, personId, enabled]);

  return useMemo<StudyDaysState>(() => {
    if (!enabled || !personId) return { status: "idle" };
    if (sessions.status === "error") return { status: "error" };
    if (sessions.status !== "ready") return { status: "loading" };
    const days = buildStudyDays({
      sessions: sessions.sessions,
      logs: logs.filter((l) => l.date >= from),
      notes: notesForLearner(notes, personId),
      cards,
      askRecords: asks,
    });
    return { status: "ready", days, today, from };
  }, [enabled, personId, sessions, logs, from, notes, cards, asks, today]);
}
